<template>
    <div class="bg-white dark:bg-neutral-800">
        <h1 class="text-xl font-bold leading-tight tracking-tight md:text-2xl text-primary mb-4">
            {{ $t('Login to your account') }}
        </h1>
        <form class="grid gap-6 mb-6 md:grid-cols-1" @submit.prevent="submitForm">
            <div>
                <label for="username" class="block mb-2 text-sm font-medium text-gray-900 dark:text-neutral-200">{{ $t('Username') }}</label>
                <input v-model="formData.username" type="text" name="username" id="username"
                    class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                    :placeholder="$t('username')" required />
            </div>
            <div>
                <label for="password" class="block mb-2 text-sm font-medium text-gray-900 dark:text-neutral-200">{{ $t('Password') }}</label>
                <div class="relative">
                    <input :type="togglePassword ? 'text' : 'password'" v-model="formData.password" name="password"
                        id="password" placeholder="••••••••"
                        class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                        required />
                    <div class="absolute right-0 bottom-0 top-0 px-3 py-3 cursor-pointer"
                        @click="togglePassword = !togglePassword">
                        <svg :class="{
                            hidden: !togglePassword,
                            block: togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12 19c.946 0 1.81-.103 2.598-.281l-1.757-1.757C12.568 16.983 12.291 17 12 17c-5.351 0-7.424-3.846-7.926-5 .204-.47.674-1.381 1.508-2.297L4.184 8.305c-1.538 1.667-2.121 3.346-2.132 3.379-.069.205-.069.428 0 .633C2.073 12.383 4.367 19 12 19zM12 5c-1.837 0-3.346.396-4.604.981L3.707 2.293 2.293 3.707l18 18 1.414-1.414-3.319-3.319c2.614-1.951 3.547-4.615 3.561-4.657.069-.205.069-.428 0-.633C21.927 11.617 19.633 5 12 5zM16.972 15.558l-2.28-2.28C14.882 12.888 15 12.459 15 12c0-1.641-1.359-3-3-3-.459 0-.888.118-1.277.309L8.915 7.501C9.796 7.193 10.814 7 12 7c5.351 0 7.424 3.846 7.926 5C19.624 12.692 18.76 14.342 16.972 15.558z" />
                        </svg>
                        <svg :class="{
                            hidden: togglePassword,
                            block: !togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12,9c-1.642,0-3,1.359-3,3c0,1.642,1.358,3,3,3c1.641,0,3-1.358,3-3C15,10.359,13.641,9,12,9z" />
                            <path
                                d="M12,5c-7.633,0-9.927,6.617-9.948,6.684L1.946,12l0.105,0.316C2.073,12.383,4.367,19,12,19s9.927-6.617,9.948-6.684 L22.054,12l-0.105-0.316C21.927,11.617,19.633,5,12,5z M12,17c-5.351,0-7.424-3.846-7.926-5C4.578,10.842,6.652,7,12,7 c5.351,0,7.424,3.846,7.926,5C19.422,13.158,17.348,17,12,17z" />
                        </svg>
                    </div>
                </div>
                <div class="mt-2 text-right">
                    <a :href="forgotPasswordUrl" class="text-sm text-gray-00 dark:text-neutral-500 hover:underline">
                        {{ $t('Forgot password') }}?
                    </a>
                </div>
            </div>
            <div class="md:col-span-1">
                <button type="submit"
                    class="w-full text-white bg-gradient hover:bg-primary-700 hover:scale-105 transition active:scale-90 font-medium rounded-lg text-sm px-5 py-3 text-center"
                    :disabled="loading">
                    <span v-if="loading">{{ $t('Logging in') }}...</span>
                    <span v-else>{{ $t('Login') }}</span>
                </button>
            </div>
            <div class="md:col-span-1">
                <p class="text-sm font-light text-gray-500">
                    {{ $t("Don't have an account") }}?
                    <a :href="registerUrl" class="font-medium text-primary-600 hover:underline">
                        {{ $t('Register here') }}
                    </a>
                </p>
            </div>
            <div v-if="msg['errorMessage']" class="md:col-span-1">
                <p class="text-sm text-red-600">{{ msg['errorMessage'] }}</p>
            </div>
        </form>
    </div>
</template>

<script>

export default {
    data() {
        return {
            formData: {
                username: '',
                password: '',
            },
            msg: [],
            togglePassword: false,
            registerUrl: "/register",
            forgotPasswordUrl: "/password/reset",
            loading: false,
            success: false,
        };
    },
    methods: {
        async submitForm() {
            this.loading = true;
            this.msg['errorMessage'] = '';
            try {
                const response = await fetch("/login", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute("content"),
                    },
                    body: JSON.stringify(this.formData),
                });
                if (!response.ok) {
                    let error = {};
                    try {
                        error = await response.json();
                    } catch (e) {
                        error = { message: this.$t("Login failed") };
                    }
                    this.msg['errorMessage'] =
                        error.message ||
                        (error.errors &&
                            Object.values(error.errors).flat().join(" ")) ||
                        this.$t("Login failed");
                    this.success = false;
                } else {
                    window.location.reload();
                    this.success = true;
                }
            } catch (e) {
                this.msg['errorMessage'] = this.$t("Login failed");
                this.success = false;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
